'use client'

// React Imports
import { useState } from 'react'
import type { FormEvent } from 'react'

// Next Imports
import { useRouter } from 'next/navigation'

// MUI Imports
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'

// Third-party Imports
import classnames from 'classnames'
import { TVirtApiError } from 'trantor-tvirt-api-js'

// Component Imports
import Link from '@components/Link'
import Logo from '@components/layout/shared/Logo'
import LanguageDropdown from '@components/layout/shared/LanguageDropdown'
import ForcePasswordChangeDialog from '@components/user/ForcePasswordChangeDialog'
import { brandify } from '@components/Brand'
import CustomTextField from '@core/components/mui/TextField'

// Config Imports
import themeConfig from '@configs/themeConfig'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'
import { useTranslation } from '@/i18n'
import { useExitTransition } from '@components/useExitTransition'

// Util Imports
import { apiClient, apiClientWithToken } from '@/libs/api'
import { setSession } from '@/utils/session'
import type { WebuiSession } from '@/utils/session'
import { describeLoadError } from '@/utils/apiError'

// Styled Custom Components
const LoginBackground = styled('div')({
  backgroundImage: 'url(/images/pages/login-background.webp)',
  backgroundSize: 'cover',
  backgroundPosition: 'center'
})

// IDENTIFIER-FIRST login (ratified 2026-07-31, the Google/Entra pattern). Three
// steps, and the split is not cosmetic:
//
//   1) 'username' — POST /auth/identify answers THE NEXT STEP and nothing else
//      (#315, 2026-08-09): a password, or a redirect to an external IdP. It says
//      nothing about whether the account exists (an unknown name gets exactly the
//      same answer), and since #315 it does not name the realm either — which realm
//      resolved the identifier IS the cluster's federation map, and step 1 is
//      unauthenticated. This page must not leak either fact: no branch, no different
//      rendering, no early failure. Whatever comes back, step 2 renders identically
//      and only ITS submit can fail.
//   2) 'password' — the password, under the identifier the user typed. The realm is
//      the server's business: it derives it from the domain (or from the cluster's
//      own name for a bare username) and tells the session about it afterwards.
//   3) 'otp' — reached ONLY when the server answers the login with 401
//      `mfa_required`: the password was already accepted at that point (deliberate
//      and safe), and the account owes a second factor. The same field takes a
//      TOTP code or a recovery code — they are interchangeable.
type Step = 'username' | 'password' | 'otp'

const Login = () => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [step, setStep] = useState<Step>('username')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Login accepted but mustChangePassword is set: the session is parked here (no
  // cookie yet) until the forced change succeeds — or the user aborts the login.
  const [pendingChange, setPendingChange] = useState<WebuiSession | null>(null)

  // Hooks
  const router = useRouter()
  const { settings } = useSettings()
  const { t } = useTranslation()

  // Exit-animation sweep (Luca 2026-07-24): the parked session is retained
  // through the fade so the dialog dissolves whole when the login is aborted.
  const pendingExit = useExitTransition(pendingChange)

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  // Back to step 1 to fix a typo: everything typed after the identifier goes with
  // it — the next identify must decide from scratch.
  const restart = () => {
    setStep('username')
    setPassword('')
    setOtp('')
    setError(null)
  }

  const describeError = (err: unknown): string => {
    if (err instanceof TVirtApiError && err.status === 401) {
      return t('login.invalidCredentials')
    }

    if (err instanceof TVirtApiError && err.status === 429) {
      return t('login.tooManyAttempts')
    }

    if (err instanceof TVirtApiError && err.status === 503) {
      return t('login.realmUnavailable')
    }

    return describeLoadError(err, t, 'login.genericError')
  }

  // Step 1. The ONLY failures here are transport ones (unreachable backend, rate
  // limit): the server answers a stranger and a real account identically, so
  // nothing about the account can be reported.
  const handleIdentify = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const resolved = await apiClient().auth.identify(username)

      // Reserved for OIDC/SAML (rounds 2 and 3) and impossible today — handled
      // defensively rather than built upon.
      if (resolved.method === 'redirect' && resolved.redirectUrl !== undefined) {
        window.location.assign(resolved.redirectUrl)

        return
      }

      setStep('password')
      setSubmitting(false)
    } catch (err) {
      setError(describeError(err))
      setSubmitting(false)
    }
  }

  // Steps 2 and 3 share one submit: the second factor is the SAME login, resent
  // with the code the server asked for.
  const enter = async (code?: string) => {
    setError(null)
    setSubmitting(true)

    try {
      // No realm goes up with the login: `mario.rossi@example.com` carries its own,
      // a bare name defaults to the mounted cluster's, and step 1 never told us
      // which one resolved — deliberately (#315).
      const created = await apiClient().auth.login(username, password, undefined, code)

      const webuiSession: WebuiSession = {
        token: created.token,
        username: created.user.username,
        realm: created.user.realm,
        displayName: created.user.displayName
      }

      // The mustChangePassword gate (contract §users): everything except the session
      // endpoints and the self password change would 403 — force the change first.
      if (created.mustChangePassword === true) {
        setPendingChange(webuiSession)
        setSubmitting(false)

        return
      }

      // Session cookie (dies with the browser — ratified 2026-07-12): opaque tvts_
      // token plus the identity the dashboard shows in the user dropdown.
      setSession(webuiSession)
      router.push(themeConfig.homePageUrl)
    } catch (err) {
      // The password was accepted and the account owes a second factor: ask for it
      // instead of reporting a failure.
      if (err instanceof TVirtApiError && err.status === 401 && err.code === 'mfa_required') {
        setStep('otp')
        setOtp('')
        setSubmitting(false)

        return
      }

      setError(describeError(err))
      setSubmitting(false)
    }
  }

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault()
    await enter()
  }

  const handleOtp = async (e: FormEvent) => {
    e.preventDefault()
    await enter(otp)
  }

  return (
    <div className='flex bs-full justify-center'>
      <LoginBackground
        className={classnames('flex bs-full flex-1 min-bs-[100dvh] relative max-md:hidden', {
          'border-ie': settings.skin === 'bordered'
        })}
      />
      <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
        <Link className='login-logo absolute block-start-5 sm:block-start-[33px] inline-start-6 sm:inline-start-[38px]'>
          <Logo />
        </Link>
        <div className='absolute block-start-5 sm:block-start-[25px] inline-end-6'>
          <LanguageDropdown />
        </div>
        <div className='flex flex-col gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 sm:mbs-14 md:mbs-0'>
          <div className='flex flex-col gap-1'>
            <Typography variant='h4'>{brandify(t('login.welcome').replace('{app}', themeConfig.templateName))}</Typography>
            <Typography>{step === 'otp' ? t('login.otpSubtitle') : t('login.subtitle')}</Typography>
          </div>
          {step === 'username' && (
            <form noValidate autoComplete='off' onSubmit={handleIdentify} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.username')}
                placeholder={t('login.usernamePlaceholder')}
                helperText={t('login.usernameHint')}
                value={username}
                onChange={e => setUsername(e.target.value)}
                slotProps={{ htmlInput: { 'aria-label': 'login-username' } }}
              />
              <Button fullWidth variant='contained' type='submit' disabled={submitting || username.trim() === ''}>
                {t('login.continue')}
              </Button>
            </form>
          )}
          {/* The identifier the user typed, and the way back to change it. It is what
              they already know — never the realm it resolved to (#315). */}
          {step !== 'username' && (
            <div className='flex items-center gap-2'>
              <Chip label={username} size='small' variant='tonal' color='primary' aria-label='login-identifier' />
              <Button size='small' variant='text' aria-label='login-change-user' onClick={restart}>
                {t('login.changeUser')}
              </Button>
            </div>
          )}
          {step === 'password' && (
            <form noValidate autoComplete='off' onSubmit={handlePassword} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.password')}
                placeholder='············'
                id='outlined-adornment-password'
                type={isPasswordShown ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                slotProps={{
                  htmlInput: { 'aria-label': 'login-password' },
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton edge='end' onClick={handleClickShowPassword} onMouseDown={e => e.preventDefault()}>
                          <i className={isPasswordShown ? 'tabler-eye-off' : 'tabler-eye'} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
              <div className='flex justify-end'>
                <Typography className='text-end' color='primary.main' component={Link}>
                  {t('login.forgotPassword')}
                </Typography>
              </div>
              <Button fullWidth variant='contained' type='submit' disabled={submitting}>
                {t('login.submit')}
              </Button>
            </form>
          )}
          {step === 'otp' && (
            <form noValidate autoComplete='off' onSubmit={handleOtp} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.otp')}
                placeholder='123456'
                value={otp}
                helperText={t('login.otpHint')}
                onChange={e => setOtp(e.target.value)}
                slotProps={{ htmlInput: { 'aria-label': 'login-otp', autoComplete: 'one-time-code', inputMode: 'text' } }}
              />
              <Button fullWidth variant='contained' type='submit' disabled={submitting || otp.trim() === ''}>
                {t('login.otpSubmit')}
              </Button>
            </form>
          )}
        </div>
      </div>
      {pendingExit.mounted !== null && (
        <ForcePasswordChangeDialog
          open={pendingExit.open}
          onExited={pendingExit.onExited}
          onCancel={() => setPendingChange(null)}
          onSubmit={async newPassword => {
            const parked = pendingExit.mounted

            if (parked === null) {
              return
            }

            // The login password just typed is the current one; the parked token
            // authenticates the self change, then the cookie lands and we enter.
            await apiClientWithToken(parked.token).users.changeMyPassword(password, newPassword)
            setSession(parked)
            router.push(themeConfig.homePageUrl)
          }}
        />
      )}
    </div>
  )
}

export default Login
